/** @jsxImportSource @emotion/react */
import type { PropsWithChildren } from 'react';
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import type { Interpolation, Theme } from '@emotion/react';
import { css } from '@emotion/react';
import type { GlobalToken } from 'antd';
import Typography, { TypographyVariant } from '../Typography';
import type { ITheme, ThemeColor } from '../../styles/theme';
import { BREAKPOINT, BREAKPOINT_BOUNDARIES_MIN } from '../../utils/breakpoints';
import type { IPropsWithTestId } from '../../../constants/data-element-tracking-ids';
import { BoxWithId } from '../BoxWithId/BoxWithId';
import { Button, ButtonVariant } from '../Button';

export enum AlertBannerVariant {
  Default,
  Positive,
  Negative,
  Info,
  Dark,
}

interface IProps extends IPropsWithTestId {
  variant?: AlertBannerVariant;
  open?: boolean;
  theme: ITheme;
  themeToken: GlobalToken;
  additionalStyles?: Interpolation<Theme>;
  isClosable?: boolean;
  onClose?: () => void;
}

const AlertBanner: React.FC<PropsWithChildren<IProps>> = ({
  children,
  variant = AlertBannerVariant.Default,
  open = true,
  theme,
  themeToken,
  isClosable = true,
  onClose,
  dataElementId,
  additionalStyles = {},
}) => {
  const [isExisting, setExisting] = useState(open);

  const getVariantSettings = (
    backgroundColor: ThemeColor,
    titleColor: ThemeColor = theme.colors.grayscale.white,
  ) => ({
    styles: {
      backgroundColor,
    },
    titleColor,
    titleIcon: 'fa-solid fa-circle-exclamation',
    iconColor: titleColor,
  });
  const variants = {
    [AlertBannerVariant.Default]: getVariantSettings(
      theme.colors.bannerYellow,
      theme.colors.grayscale.black,
    ),
    [AlertBannerVariant.Positive]: getVariantSettings(theme.colors.positive),
    [AlertBannerVariant.Negative]: getVariantSettings(
      theme.colors.negativeHover,
    ),
    [AlertBannerVariant.Info]: getVariantSettings(theme.colors.neutral),
    [AlertBannerVariant.Dark]: getVariantSettings(theme.colors.grayscale.black),
  };
  const variantProperties = variants[variant];

  const LARGE_DESKTOP_BREAKPOINT = 1286;

  const BANNER_PADDING = 12;
  const CLOSED_ICON_WIDTH = 44;
  const CLOSED_ICON_MARGIN_FROM_TEXT = 8;
  const CLOSED_ICON_PADDING_RIGHT_LARGE_DESKTOP = 77 - BANNER_PADDING;
  const CLOSED_ICON_PADDING_RIGHT =
    CLOSED_ICON_MARGIN_FROM_TEXT - BANNER_PADDING;

  const alertBannerClasses = css`
    width: 100%;
    padding: ${BANNER_PADDING}px;
    display: flex;
    box-sizing: border-box;
    align-items: center;
    ${variantProperties.styles}
  `;

  const textWrapperStyles = css`
    width: 100%;
    display: flex;
    justify-content: center;
    text-align: center;
    ${isClosable
    ? `
      margin-left: ${
  CLOSED_ICON_MARGIN_FROM_TEXT +
        CLOSED_ICON_WIDTH +
        CLOSED_ICON_PADDING_RIGHT_LARGE_DESKTOP
}px;
      @media (max-width: ${LARGE_DESKTOP_BREAKPOINT}px) {
        margin-left: ${
  CLOSED_ICON_MARGIN_FROM_TEXT +
          CLOSED_ICON_WIDTH +
          CLOSED_ICON_PADDING_RIGHT
}px;
      }
      @media (max-width: ${
  BREAKPOINT_BOUNDARIES_MIN[BREAKPOINT.TABLET] - 1
}px) {
        margin-left: 0px;
      }
    `
    : ''}
    * > p {
      margin: 0px;
    }
    * > a {
      color: inherit;
      text-decoration-thickness: from-font;
    }
  `;

  const iconWrapperStyles = css`
    width: ${CLOSED_ICON_WIDTH}px;
    height: 32px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: ${CLOSED_ICON_MARGIN_FROM_TEXT}px;
    margin-right: ${CLOSED_ICON_PADDING_RIGHT_LARGE_DESKTOP}px;
    @media (max-width: ${LARGE_DESKTOP_BREAKPOINT}px) {
      margin-right: ${CLOSED_ICON_PADDING_RIGHT}px;
    }
  `;

  const iconStyles = css`
    cursor: pointer;
    border: 1px black;
    color: ${variantProperties.iconColor};
  `;

  const handleIconClick = () => {
    if (onClose) {
      onClose();
    }
    setExisting(false);
  };

  if (!isExisting || !open) {
    return null;
  }

  return (
    <BoxWithId
      css={[alertBannerClasses, additionalStyles]}
      role="note"
      dataElementId={dataElementId}
    >
      <div css={textWrapperStyles}>
        <Typography
          variant={{
            [BREAKPOINT.DESKTOP]: TypographyVariant.MEDIUM_BODY_REGULAR,
            [BREAKPOINT.MOBILE]: TypographyVariant.SMALL_BODY_REGULAR,
          }}
          color={variantProperties.titleColor}
          additionalStyles={{ maxWidth: '1280px' }}
        >
          {children}
        </Typography>
      </div>
      {isClosable && (
        <Button
          themeToken={themeToken}
          type="button"
          variant={ButtonVariant.FLAT}
          additionalStyles={iconWrapperStyles}
          onClick={handleIconClick}
          dataElementId={`${dataElementId}-close-icon`}
          color={variantProperties.iconColor}
        >
          <FontAwesomeIcon icon={faXmark} css={iconStyles} />
        </Button>
      )}
    </BoxWithId>
  );
};

export default AlertBanner;
